import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agreementsApi } from '../utils/api';
import Layout from '../components/Layout';

export default function Agreements() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      const response = await agreementsApi.list();
      setAgreements(response.data.agreements);
    } catch (error) {
      console.error('Failed to fetch agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="page-header">Agreements</h1>
        <p className="text-gray-600 mt-1">Signed, immutable agreements with your customers</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : agreements.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Agreement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Counterparty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Margin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Signed</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agreements.map((agreement) => {
                const revenue = parseFloat(agreement.total_booked || 0);
                const cost = parseFloat(agreement.total_cost || 0);
                const margin = revenue - cost;
                const marginPct = revenue > 0 ? Math.round((margin / revenue) * 100) : 0;
                const isAmended = agreement.status === 'amended';
                const isAmendment = !!agreement.amends_agreement_id;
                const myRole = agreement.myRole || 'seller';
                const counterparty = agreement.counterparty || {};
                const isBuyerView = myRole === 'buyer';

                return (
                  <tr key={agreement.id} className={`hover:bg-gray-50 ${isAmended ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Link to={`/agreements/${agreement.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                          {agreement.name}
                        </Link>
                        {isAmendment && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-600 font-medium">Amendment</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{counterparty.name || agreement.customer_name || '—'}</div>
                      {counterparty.slug && (
                        <div className="text-xs text-gray-600">{counterparty.slug}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        myRole === 'seller' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {myRole === 'seller' ? 'Agency' : 'Client'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isBuyerView ? (
                        <span className="text-sm text-gray-600" title="Unit economics are agency-owned; ask the agency to share">—</span>
                      ) : revenue > 0 ? (
                        <span className={`text-sm font-medium ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(margin)} ({marginPct}%)
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        isAmended ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {isAmended ? 'Amended' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {agreement.signed_at ? formatDate(agreement.signed_at) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No agreements yet</h3>
          <p className="mt-2 text-gray-600">Agreements are created when a proposal is signed.</p>
          <Link to="/proposals" className="btn-primary mt-6 inline-flex">
            View Proposals
          </Link>
        </div>
      )}
    </Layout>
  );
}
